import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { Autocomplete, Stack, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Switch from 'react-switch';
import DatePickerFullScreenDialog from './DatePickerFullScreenDialog';
import OrigenDestinoFullScreenDialog from './OrigenDestinoFullScreenDialog';
import PasajerosFullScreenDialog from './PasajerosFullScreenDialog';
import PasajerosSelector from './PasajerosSelector';
import '../../styles/header.css';
import './styles.css';
import ErrorSnackbar from '../ErrorSnackbar';
import { dataDefaultValues } from 'frontend/constants';
import PackagesApi from 'frontend/api/paquetes';
import { useColors } from 'frontend/hooks/useColors';
import DateRangeSelector from './DateRangeSelector';
import React from 'react';
import debounce from 'lodash/debounce';

function safeDate(val) {
  if (!val) return undefined;
  const d = (val instanceof Date) ? val : new Date(val);
  return isNaN(d) ? undefined : d;
}

const FlightsFilterForm = ({
  handleOriginSelect,
  handleDestinationSelect,
  handleDateRangeSelect,
  handleOccupationChange,
  handleFlightPassenger,
  formInput,
  isDestinationEmpty,
  setIsDestinationEmpty,
  isOriginEmpty,
  setIsOriginEmpty,
  isDatesEmpty,
  isTuristasEmpty,
  setIsDatesEmpty,
  handleIsOnlyOneWay,
}) => {
  const { t } = useTranslation('translations');
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [focusedInput, setFocusedInput] = useState(null);

  const [totalTourists, setTotalTourists] = useState(1);

  const [adultsQuantity, setAdultsQuantity] = useState(1);
  const [childrenQuantity, setChildrenQuantity] = useState(0);
  const [babiesQuantity, setBabiesQuantity] = useState(0);
  const [passengerAges, setPassengerAges] = useState([30]);
  const [errorMessage, setErrorMessage] = useState('');
  const [openToast, setOpenToast] = useState(false);

  const maxPassengers = 9;

  const totalPassengers = adultsQuantity + childrenQuantity + babiesQuantity;

  const [isDestinationOpen, setIsDestinationOpen] = useState(false);
  const originInputRef = useRef(null);
  const destinationInputRef = useRef(null);
  const [selectedOrigin, setSelectedOrigin] = useState(formInput.origin ?? null);
  const [selectedDestination, setSelectedDestination] = useState(formInput.destination ?? null);
  const [isMobile, setIsMobile] = useState(false);
  const [isDestinationDialogOpen, setIsDestinationDialogOpen] = useState(false);
  const [isFullScreenRangeDatePickerOpen, setIsFullScreenRangeDatePickerOpen] = useState(false);
  const [shouldPasajerosOpen, setShouldPasajerosOpen] = useState(false);
  const [flightOrigins, setFlightOrigins] = useState(dataDefaultValues);
  const [flightDestinations, setFlightDestinations] = useState(dataDefaultValues);

  const [searchOrigin, setSearchOrigin] = useState('');
  const [searchDestination, setSearchDestination] = useState('');

  const [query, setQuery] = useState('');

  const [llegoData, setLlegoData] = useState({
    origin: null,
    destination: null,
    originId: null,
    destinationID: null,
    passengers: passengerAges,
    departureDate: null,
    returnDate: null,
    direct: false,
    baggage: false,
    cabin: 'Y',
    currency: 'USD',
  });

  const [isOnlyOneWay, setIsOnlyOneWay] = useState(formInput?.isOnlyOneWay ?? false);

  // Refs para controlar el flujo
  const isInitialMount = useRef(true);
  const isUpdatingFromFormInput = useRef(false);
  const prevStartDate = useRef(null);
  const prevEndDate = useRef(null);

  const handleLlegoData = useCallback(
    (passengers, startDate, endDate) => {
      setLlegoData((state) => ({
        ...state,
        passengers,
        departureDate: startDate,
        returnDate: endDate,
      }));
      handleFlightPassenger(passengers);
    },
    [handleFlightPassenger],
  );

  const fetchAirports = async (value, isOrigin = true) => {
    try {
      const response = await PackagesApi.getAirports({ cityName: value });
      
      if (isOrigin) {
        setFlightOrigins({ isLoading: false, error: false, data: response });
      } else {
        setFlightDestinations({ isLoading: false, error: false, data: response });
      }
    } catch (error) {
      showError(error?.message);
      if (isOrigin) {
        setFlightOrigins({ data: [], error: true, isLoading: false });
      } else {
        setFlightDestinations({ data: [], error: true, isLoading: false });
      }
    }
  };

  const handleAirportChange = async (_, value, isOrigin) => {
    if (!value) {
      if (isOrigin) {
        setSearchOrigin('');
        setFlightOrigins({ isLoading: false, error: false, data: [] });
      } else {
        setSearchDestination('');
        setFlightDestinations({ isLoading: false, error: false, data: [] });
      }
      return;
    }

    if (isOrigin) {
      setSearchOrigin(value);
      setFlightOrigins({ isLoading: true, error: false, data: [] });
    } else {
      setSearchDestination(value);
      setFlightDestinations({ isLoading: true, error: false, data: [] });
    }

    try {
      await fetchAirports(value, isOrigin);
    } catch (error) {
      console.error('Error fetching airports:', error);
    }
  };

  const debouncedAirportChange = React.useCallback(
    debounce((value, isOrigin) => {
      handleAirportChange(null, value, isOrigin);
    }, 300),
    []
  );

  const handleSearchChange = (value, isOrigin) => {
    if (isOrigin) {
      setSearchOrigin(value);
    } else {
      setSearchDestination(value);
    }
    debouncedAirportChange(value, isOrigin);
  };

  const { background, text } = useColors();

  const handleChangeOrigin = (value) => {
    handleOriginSelect(value);
    setSelectedOrigin(value);
    setIsOriginEmpty(false);
    if (originInputRef.current) originInputRef.current.blur();

    if (destinationInputRef.current && value) {
      setTimeout(() => {
        destinationInputRef.current.focus();
      }, 200);
    }

    if (value) setIsDestinationOpen(true);
  };

  const handleChangeDestination = (value) => {
    handleDestinationSelect(value);
    setSelectedDestination(value);
    setIsDestinationEmpty(false);
    if (value) {
      setIsDestinationOpen(false);
      if (!startDate) {
        setFocusedInput('startDate');
        setIsFullScreenRangeDatePickerOpen(true);
      } else if (startDate && !endDate && !formInput.isOnlyOneWay) {
        setFocusedInput('endDate');
        setIsFullScreenRangeDatePickerOpen(true);
      }
    }
  };

  const handleSwitchChange = (checked) => {
    setIsOnlyOneWay(checked);
    handleIsOnlyOneWay(checked);
    if (checked) {
      setEndDate(null);
    }
  };

  const showError = useCallback((message) => {
    setErrorMessage(message);
    setOpenToast(true);
  }, []);

  const handleCloseToast = useCallback(() => {
    setOpenToast(false);
  }, []);

  const handleAdultsChange = useCallback(
    (value) => {
      const newQuantity = parseInt(value, 10) || 0;
      if (newQuantity + childrenQuantity + babiesQuantity <= maxPassengers) {
        setAdultsQuantity(newQuantity);
      } else {
        showError(`El número total de pasajeros no puede superar ${maxPassengers}.`);
      }
    },
    [childrenQuantity, babiesQuantity, showError],
  );

  const handleChildrenChange = useCallback(
    (value) => {
      const newQuantity = parseInt(value, 10) || 0;
      if (adultsQuantity + newQuantity + babiesQuantity <= maxPassengers) {
        setChildrenQuantity(newQuantity);
      } else {
        showError(`El número total de pasajeros no puede superar ${maxPassengers}.`);
      }
    },
    [adultsQuantity, babiesQuantity, showError],
  );

  const handleBabiesChange = useCallback(
    (value) => {
      const newQuantity = parseInt(value, 10) || 0;
      if (adultsQuantity + childrenQuantity + newQuantity <= maxPassengers) {
        setBabiesQuantity(newQuantity);
      } else {
        showError(`El número total de pasajeros no puede superar ${maxPassengers}.`);
      }
    },
    [adultsQuantity, childrenQuantity, showError],
  );

  // Initialize dates from formInput FIRST - SOLO inicializar, no comunicar
  useEffect(() => {
    const {
      departureDate: newDeparture,
      returnDate: newReturn,
      isOnlyOneWay: newIsOnlyOneWay
    } = formInput;

    // Marcar que estamos actualizando desde formInput
    isUpdatingFromFormInput.current = true;

    setStartDate(newDeparture);
    if (!newIsOnlyOneWay) {
      setEndDate(newReturn);
    } else {
      setEndDate(null);
    }

    // Después de actualizar, permitir comunicación
    setTimeout(() => {
      isUpdatingFromFormInput.current = false;
      isInitialMount.current = false;
    }, 0);
  }, [formInput.departureDate, formInput.returnDate, formInput.isOnlyOneWay]);

  // Handle date range selection - SOLO cuando el usuario cambia las fechas
  useEffect(() => {
    // No ejecutar si:
    // 1. Es el mount inicial
    // 2. Estamos actualizando desde formInput
    // 3. Las fechas no han cambiado realmente
    if (
      isInitialMount.current || 
      isUpdatingFromFormInput.current ||
      (startDate === prevStartDate.current && endDate === prevEndDate.current)
    ) {
      prevStartDate.current = startDate;
      prevEndDate.current = endDate;
      return;
    }

    // Solo llamar handleDateRangeSelect cuando el usuario cambia las fechas
    handleDateRangeSelect(startDate, endDate);

    if (formInput.isOnlyOneWay && startDate) {
      handleDateRangeSelect(startDate, null);
      setFocusedInput(null);
    }

    // Actualizar refs
    prevStartDate.current = startDate;
    prevEndDate.current = endDate;
  }, [startDate, endDate, formInput.isOnlyOneWay]);

  // Handle Llego data - también needs control
  useEffect(() => {
    if (passengerAges.length > 0 && !isInitialMount.current && !isUpdatingFromFormInput.current) {
      const startOnly = startDate ? new Date(startDate).toISOString().split('T')[0] : null;
      const endOnly = !formInput.isOnlyOneWay && endDate ? new Date(endDate).toISOString().split('T')[0] : null;
      
      handleLlegoData(passengerAges, startOnly, endOnly);
    }
  }, [passengerAges, startDate, endDate, formInput.isOnlyOneWay]);

  // Clear dates empty flag when dates are selected
  useEffect(() => {
    if (startDate && endDate) setIsDatesEmpty(false);
  }, [startDate, endDate]);

  // Handle responsive behavior
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Update origin from formInput
  useEffect(() => {
    setSelectedOrigin(formInput.origin ?? null);
  }, [formInput.origin]);

  // Update destination from formInput
  useEffect(() => {
    setSelectedDestination(formInput.destination ?? null);
  }, [formInput.destination]);

  // Update total tourists when totalPassengers changes
  useEffect(() => {
    setTotalTourists(totalPassengers);
  }, [totalPassengers]);

  // Communicate occupation change when totalTourists changes
  useEffect(() => {
    if (totalTourists > 0) {
      handleOccupationChange(totalTourists);
    }
  }, [totalTourists]);

  // Update passenger ages when quantities change
  useEffect(() => {
    const adultsAges = Array(adultsQuantity).fill(30);
    const childrenAges = Array(childrenQuantity).fill(7);
    const babiesAges = Array(babiesQuantity).fill(1);

    setPassengerAges([...adultsAges, ...childrenAges, ...babiesAges]);
  }, [adultsQuantity, childrenQuantity, babiesQuantity]);

  // Initialize passengers from URL
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const passengersParam = urlParams.get('passengers');

    if (passengersParam) {
      try {
        const decodedPassengers = decodeURIComponent(passengersParam);
        const passengersArray = decodedPassengers.split(',').map(Number);

        if (passengersArray.length > 0) {
          const adults = passengersArray.filter((age) => age === 30).length;
          const children = passengersArray.filter((age) => age === 7).length;
          const babies = passengersArray.filter((age) => age === 1).length;

          setAdultsQuantity(adults || 1);
          setChildrenQuantity(children || 0);
          setBabiesQuantity(babies || 0);
        }
      } catch (error) {
        console.error('Error parsing URL passengers:', error);
        setAdultsQuantity(1);
      }
    }
  }, []);

  // Ensure minimum passengers
  useEffect(() => {
    if (adultsQuantity === 0 && childrenQuantity === 0 && babiesQuantity === 0) {
      setAdultsQuantity(1);
    }
  }, [adultsQuantity, childrenQuantity, babiesQuantity]);

  return (
    <>
      <Stack className="input-container">
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Desde dónde?</h3>
        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            onSelect={handleOriginSelect}
            options={flightOrigins?.data}
            originSelected={selectedOrigin}
            isOriginEmpty={isOriginEmpty}
            setIsOriginEmpty={setIsOriginEmpty}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setSearchValue={setSearchOrigin}
            handleAirportChange={(_, value) => handleSearchChange(value, true)}
            isFlight
            searchValue={searchOrigin}
            isLoading={flightOrigins?.isLoading}
          />
        ) : (
          <Autocomplete
            id="origin-autocomplete"
            className={`form__field origin-typeahead ${isOriginEmpty ? 'autocomplete-error' : ''}`}
            noOptionsText="No hay resultados"
            size="small"
            value={selectedOrigin}
            onInputChange={(_, value) => handleSearchChange(value, true)}
            defaultValue={formInput.origin}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            onFocus={() => setIsOriginEmpty(false)}
            options={flightOrigins?.data}
            loading={flightOrigins?.isLoading}
            style={{ width: '100%' }}
            loadingText="Cargando orígenes"
            onChange={(_, v) => {
              handleChangeOrigin(v);
            }}
            getOptionLabel={(option) => {
              return `${option.airportName} (${option.airportCode}) - ${option.cityName}`;
            }}
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(input) => (originInputRef.current = input)}
                className="focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent"
                onFocus={() => {
                  const originInput = document.querySelector('.origin-typeahead');
                  if (originInput) {
                    const closeBtn = originInput.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                  handleOriginSelect(undefined);
                }}
              />
            )}
          />
        )}
        <Stack
          flexDirection={'row'}
          alignSelf={'flex-end'}
          alignItems={'center'}
          zIndex={isOriginEmpty ? 1 : -1}
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography fontSize={11} color={'rgb(207, 24, 0)'}>
            Ingresa tu origen
          </Typography>
        </Stack>
      </Stack>

      <Stack className="input-container">
        <h3 style={{ fontSize: 12, color: '#4f4f4f' }}>¿Hasta dónde?</h3>

        {isMobile ? (
          <OrigenDestinoFullScreenDialog
            onSelect={handleChangeDestination}
            options={flightDestinations?.data}
            originSelected={selectedDestination}
            disabled={!selectedOrigin}
            isDestinationEmpty={isDestinationEmpty}
            setIsDestinationEmpty={setIsDestinationEmpty}
            isDestination
            isOpen={isDestinationDialogOpen}
            setIsDestinationDialogOpen={setIsDestinationDialogOpen}
            setIsFullScreenRangeDatePickerOpen={setIsFullScreenRangeDatePickerOpen}
            handleAirportChange={(_, value) => handleSearchChange(value, false)}
            setSearchValue={setSearchDestination}
            handlePlaceSelect={handleDestinationSelect}
            isFlight
            searchValue={searchDestination}
            isLoading={flightDestinations?.isLoading}
          />
        ) : (
          <Autocomplete
            id="destination-autocomplete"
            className={`form__field destination-typeahead ${
              isDestinationEmpty ? 'autocomplete-error' : ''
            }`}
            noOptionsText="No hay resultados"
            size="small"
            value={selectedDestination}
            onInputChange={(_, value) => handleSearchChange(value, false)}
            defaultValue={formInput.destination}
            isOptionEqualToValue={(option, value) => option.name === value.name}
            onFocus={() => setIsDestinationEmpty(false)}
            options={flightDestinations?.data}
            loading={flightDestinations?.isLoading}
            style={{ width: '100%' }}
            loadingText="Cargando destinos"
            onChange={(e, v) => {
              handleChangeDestination(v);
            }}
            getOptionLabel={(option) =>
              `${option.airportName} (${option.airportCode}) - ${option.cityName}`
            }
            clearOnBlur
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={(input) => (destinationInputRef.current = input)}
                className="focus:ring-offset-0 focus:ring-0 ring-offset-0 ring-transparent"
                onFocus={() => {
                  const input = document.querySelector('.destination-typeahead');
                  if (input) {
                    const closeBtn = input.querySelector('.MuiAutocomplete-clearIndicator');
                    if (closeBtn) {
                      closeBtn.click();
                    }
                  }
                  handleDestinationSelect(undefined);
                }}
              />
            )}
          />
        )}

        <Stack
          flexDirection={'row'}
          alignSelf={'flex-end'}
          alignItems={'center'}
          zIndex={isDestinationEmpty ? 1 : -1}
          className="warning-animated-message"
        >
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography fontSize={11} color={'rgb(207, 24, 0)'}>
            Ingresa tu destino
          </Typography>
        </Stack>
      </Stack>

      <Stack className={`form__field date-picker ${isDatesEmpty ? 'dates-error' : ''}`}>
        <Stack flexDirection={'row'} justifyContent={'space-between'}>
          <Stack flexDirection={'row'} alignItems={'center'} gap={1}>
            <Typography fontSize={12} color={'#4f4f4f'}>
              Solo ida
            </Typography>
            <Switch checked={isOnlyOneWay} width={32} height={16} onChange={handleSwitchChange} />
          </Stack>
        </Stack>
        {isMobile ? (
          <DatePickerFullScreenDialog
            startDate={safeDate(startDate)}
            endDate={isOnlyOneWay ? undefined : safeDate(endDate)}
            isOpen={isFullScreenRangeDatePickerOpen}
            setShouldPasajerosOpen={setShouldPasajerosOpen}
            isDatesEmpty={isDatesEmpty}
            isOnlyOneWay={isOnlyOneWay}
            onChange={({ startDate: newStart, endDate: newEnd }) => {
              setStartDate(newStart);
              setEndDate(isOnlyOneWay ? null : newEnd);
              if (!isOnlyOneWay && newEnd && newEnd !== newStart) {
                setIsFullScreenRangeDatePickerOpen(false);
              }
            }}
          />
        ) : (
          <DateRangeSelector
            startDate={safeDate(startDate)}
            endDate={isOnlyOneWay ? undefined : safeDate(endDate)}
            onChange={({ startDate: newStart, endDate: newEnd }) => {
              setStartDate(newStart);
              setEndDate(isOnlyOneWay ? null : newEnd);
            }}
            error={isDatesEmpty}
            helperText={isDatesEmpty ? (isOnlyOneWay ? 'Selecciona la fecha de ida' : 'Selecciona las fechas') : ''}
            disabled={!selectedOrigin || !selectedDestination}
            isOneWay={isOnlyOneWay}
            focusedInput={focusedInput}
            onFocusChange={setFocusedInput}
          />
        )}
        <Stack flexDirection={'row'} alignSelf={'flex-end'} alignItems={'center'} zIndex={isDatesEmpty ? 1 : -1}>
          <WarningAmberIcon style={{ fill: 'rgb(207, 24, 0)', height: 12, width: 12 }} />
          <Typography fontSize={11} color={'rgb(207, 24, 0)'}>
            Selecciona fechas
          </Typography>
        </Stack>
      </Stack>

      <Stack className="pasajeros-input-container">
        <Typography fontSize={12} color={'#4f4f4f'}>
          ¿Cuántos viajan?
        </Typography>

        <Stack>
          {isMobile ? (
            <PasajerosFullScreenDialog
              shouldOpen={shouldPasajerosOpen}
              totalTourists={totalTourists}
              setTotalTourists={setTotalTourists}
              adultsQuantity={adultsQuantity}
              childrenQuantity={childrenQuantity}
              babiesQuantity={babiesQuantity}
              setAdultsQuantity={handleAdultsChange}
              setChildrenQuantity={handleChildrenChange}
              setBabiesQuantity={handleBabiesChange}
              errorMessage={errorMessage}
            />
          ) : (
            <PasajerosSelector
              totalTourists={totalTourists}
              isTuristasEmpty={isTuristasEmpty}
              adultsQuantity={adultsQuantity}
              childrenQuantity={childrenQuantity}
              babiesQuantity={babiesQuantity}
              setAdultsQuantity={handleAdultsChange}
              setBabiesQuantity={handleBabiesChange}
              setChildrenQuantity={handleChildrenChange}
            />
          )}
        </Stack>
        <ErrorSnackbar open={openToast} message={errorMessage} onClose={handleCloseToast} />
      </Stack>
    </>
  );
};

export default FlightsFilterForm;
